<template>
    <div>
        <button
            type="button"
            :title="buttonTitle"
            :disabled="!letter.isEmailable"
            @click="initDialog"
        >
            <kgr-icon
                icon-class="email"
                :disabled="!letter.isEmailable"
                small
                :green-dot="letter.sent !== null"
            />
        </button>

        <kgr-generic-modal
            :value="showSignableLettersModal"
            :headline="$t('Hinweis')"
            @close="showSignableLettersModal = false;"
        >
            <template #default>
                <span style="white-space: normal">
                    {{ $t('Beim Versenden dieses Dokuments per E-Mail wird der digitale Signatur-Prozess nicht unterstützt.') }}<br />
                    <template v-if="!letter.inExchangeDirectory">
                        <br />
                        {{ $t('Um den digitalen Signatur-Prozess zu starten, können Sie hier oder per Klick auf das Austauschordner-Icon das Dokument zur Bearbeitung freigeben.') }}
                        <br />
                        <br />
                    </template>
                </span>
            </template>
            <template #footer>
                <kgr-button
                    class="kgr-button--outline"
                    :value="$t('Abbrechen')"
                    @click="showSignableLettersModal = false"
                />
                <kgr-button
                    :value="$t('Dennoch per E-Mail verschicken')"
                    @click="sendEmailNoneTheLess"
                />
                <kgr-button
                    v-if="!letter.inExchangeDirectory"
                    :value="$t('Im Austauschordner freigeben')"
                    @click="addToExchange"
                />
            </template>
        </kgr-generic-modal>
        <kgr-generic-modal
            v-if="showDefaultModal && emailData"
            :value="showDefaultModal"
            :headline="$t('Nachricht versenden')"
            :dialog-styles="{
                minWidth: '40vw',
                maxWidth: '95vw',
                minHeight: '70vh',
                maxHeight: '95vh',
            }"
            :content-styles="{
                overflow: 'auto',
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                borderBottom: '1px solid var(--kgr-color-neutral)',
            }"
            :resizable="true"
            @close="closeDefaultModal"
        >
            <template #default>
                <div class="kgr-inputGroup">
                    <kgr-form-row
                        :label="$t('Empfänger')"
                        field-name="recipient"
                        :error="error"
                    >
                        <kgr-checkbox-group
                            :value="recipients"
                            @post-input="recipients = $event"
                            :options="emailData.recipients"
                            field-for-label="label"
                        />
                    </kgr-form-row>
                </div>
                <div class="kgr-inputGroup">
                    <kgr-form-row
                        :label="$t('Absender')"
                        field-name="sender"
                        :error="error"
                        class="flex-grow"
                    >
                        <select
                            v-model="sender"
                            class="kgr-select"
                        >
                            <option
                                v-for="(option, index) in emailData.senders"
                                :key="index"
                                :value="option"
                            >
                                {{ option }}
                            </option>
                        </select>
                    </kgr-form-row>
                </div>
                <div class="kgr-inputGroup">
                    <div class="kgr-inputGroup__label">
                        {{ $t('Betreff') }}
                    </div>
                    <input
                        v-model="subject"
                        type="text"
                        class="kgr-input kgr-input"
                    />
                </div>
                <div
                    class="kgr-inputGroup"
                    style="display:flex; flex-flow:column; flex:1 1 auto;"
                >
                    <div class="kgr-inputGroup__label">
                        {{ $t('Nachricht (optional)') }}
                    </div>
                    <textarea
                        v-model="message"
                        class="kgr-input kgr-input"
                        style="resize:none;"
                    ></textarea>
                </div>
                <div class="kgr-inputGroup">
                    <div class="kgr-inputGroup__label">
                        {{ $t('Anhang') }}
                    </div>
                    <input
                        :value="emailData.filename"
                        type="text"
                        readonly
                        class="kgr-input"
                    /><br />
                    <br />
                </div>

                <div
                    v-if="!showAdditionalAttachments"
                    class="kgr-inputGroup"
                >
                    <kgr-button
                        :value="$t('Zusätzliche Anhänge')"
                        @click="showAdditionalAttachments = true"
                    />
                    <kgr-icon icon-class="info-circle" :title="$t('Bitte beachten Sie, dass die Gesamtgröße der Anhänge 10 MB nicht überschreiten darf.')" />
                </div>

                <div
                    v-show="showAdditionalAttachments"
                    class="kgr-inputGroup"
                >
                    <div class="kgr-inputGroup__label">
                        {{ $t('Zusätzliche Anhänge') }}
                        <kgr-icon icon-class="info-circle" :title="$t('Bitte beachten Sie, dass die Gesamtgröße der Anhänge 10 MB nicht überschreiten darf.')" />
                    </div>
                    <kgr-drop-file
                        :error-message="error.messageForField('files')"
                        show-size
                        emit-base64-files
                        multiple
                        @files="additionalAttachmentsChanged"
                    />
                </div>
            </template>
            <template #footer>
                <kgr-button
                    class="kgr-button--outline"
                    :value="$t('Abbrechen')"
                    @click="closeDefaultModal"
                />
                <kgr-button
                    :value="$t('E-Mail senden')"
                    :disabled="!allValid"
                    @click="sendEmail"
                />
            </template>
        </kgr-generic-modal>
    </div>
</template>

<script>
import CheckboxGroup from '../../common/form/checkbox/CheckboxGroup.vue';
import ChildLetter from '../../../js/dto/letter/ChildLetter.js';
import DropFile from '../../common/form/input/DropFile.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../common/form/FormRow.vue';
import GenericModal from '../../common/modal/GenericModal.vue';
import Icon from '../../common/icon/Icon.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import axios from 'axios';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-generic-modal': GenericModal,
        'kgr-button': KgrButton,
        'kgr-form-row': FormRow,
        'kgr-checkbox-group': CheckboxGroup,
        'kgr-drop-file': DropFile,
    },
    props: {
        letter: {
            type: ChildLetter,
            required: true,
        },
    },
    data() {
        return {
            showDefaultModal: false,
            showSignableLettersModal: false,
            emailData: null,
            sender: null,
            recipients: [],
            additionalAttachments: [],
            subject: null,
            message: null,
            showAdditionalAttachments: false,
            error: new Error(),
        };
    },
    computed: {
        buttonTitle() {
            if (this.letter.sent) {
                return this.$t('Das Dokument wurde ursprünglich am {date} von {firstname} {lastname} per E-Mail versendet.', {
                    date: this.letter.sent.toLocaleDateString(),
                    firstname: this.letter.sentBy ? this.letter.sentBy.firstname : 'n',
                    lastname: this.letter.sentBy ? this.letter.sentBy.lastname : 'a',
                });
            }
            if (this.letter.isEmailable) {
                return this.$t('Dokument per E-Mail verschicken');
            }

            return this.$t('Kein Empfänger für E-Mail-Versand vorhanden');
        },
        allValid() {
            return this.recipients.length > 0 && this.sender !== null && this.subject !== null && this.subject.trim() !== '';
        },
    },
    methods: {
        closeDefaultModal() {
            this.showDefaultModal = false;
            this.emailData = null;
            this.sender = null;
            this.recipients = [];
            this.additionalAttachments = [];
            this.subject = null;
            this.message = null;
            this.showAdditionalAttachments = false;
            this.error = new Error();
        },
        additionalAttachmentsChanged(attachments) {
            this.additionalAttachments = attachments.map((attachment) => ({
                name: attachment.name,
                type: attachment.type,
                base64: attachment.base64,
            }));
        },
        sendEmailNoneTheLess() {
            this.showSignableLettersModal = false;
            this.loadEmailData();
        },
        sendEmail() {
            axios.post(`/childletters/${this.letter.id}/email`, {
                senderEmail: this.sender,
                recipientIds: this.recipients.map((r) => r.id),
                subject: this.subject,
                message: this.message,
                files: this.additionalAttachments,
            })
                .then((response) => {
                    this.emailData = response.data;
                    this.$notify.success('Die E-Mail wurde erfolgreich versendet.');
                    this.$emit('email-sent', true);
                    this.closeDefaultModal();
                })
                .catch((error) => {
                    this.error = Error.fromResponse(error.response);
                })
            ;
        },
        initDialog() {
            if (this.letter.hasSignatures) {
                this.showSignableLettersModal = true;
                return;
            }
            this.loadEmailData();
        },
        loadEmailData() {
            axios.get(`/childletters/${this.letter.id}/email`)
                .then((response) => {
                    this.emailData = response.data.data;
                    this.showDefaultModal = true;
                })
                .catch((error) => {
                    console.error('Error loading email data:', error);
                });
        },
        addToExchange() {
            axios
                .put(`/letters/${this.letter.id}/exchange-directory-visibility`)
                .then((response) => {
                    this.$emit('file-visibility', response.data.newValue);
                    this.$notify.success(`letter.visibility.toggle.success.${response.data.newValue ? 'visible' : 'hidden'}`);
                    this.showSignableLettersModal = false;
                })
                .catch(() => this.$notify.error('letter.visibility.toggle.error'));
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.kgr-input,
.kgr-select {
    background-color: var(--kgr-input-background-color) !important;
}
</style>
